'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { useTrustpilot } from '@/ui/providers/trustpilot-provider';
import { TrustpilotLogo } from './trustpilot-logo';
import { getTrustpilotStarsImageUrl } from './trustpilot-stars-image';

interface TrustpilotWidgetProps {
  height?: string;
}

// Reserved height for the widget row when the tile has no explicit height.
const DEFAULT_WIDGET_HEIGHT = '44px';

/**
 * Client tile that reproduces the official TrustBox micro-combo widget from the rating resolved
 * server-side and provided via `TrustpilotProvider`. Layout + type scale mirror Trustpilot 1:1:
 * "<customers say> <rating word> [stars] <score> out of 5 based on <reviews> [Trustpilot logo]".
 * Read synchronously (no fetch, no useEffect) so it is in the SSR HTML — no flash. Renders
 * nothing when the rating is unavailable.
 */
export function TrustpilotWidget({ height }: Readonly<TrustpilotWidgetProps>) {
  const rating = useTrustpilot();
  const t = useTranslations('TrustpilotWidget');

  if (!rating) {
    return null;
  }

  const starsImageUrl = getTrustpilotStarsImageUrl(rating.stars);

  return (
    // Full-width, non-interactive wrapper handles layout/centering and reserves the row height;
    // only the inner <a> (the actual widget content) links to Trustpilot.
    <div
      data-testid="trustpilot-widget"
      className="trustpilot-ssr-widget flex w-full items-center justify-center"
      style={{ minHeight: height ?? DEFAULT_WIDGET_HEIGHT }}
    >
      <a
        href={rating.profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[14px] text-[#191919] no-underline"
      >
        <span aria-hidden="true">{rating.customersSayLabel}</span>

        <span aria-hidden="true" className="text-[18px] font-semibold">
          {rating.starsLabel}
        </span>

        {/* eslint-disable-next-line @next/next/no-img-element -- self-hosted brand asset, not optimizable */}
        <img
          src={starsImageUrl}
          alt={t('starRatingAlt', { rating: rating.stars })}
          width={150}
          height={28}
          style={{ height: '24px', width: 'auto' }}
        />

        <span aria-hidden="true" className="whitespace-nowrap">
          {rating.basedOnLabel} <span className="font-bold underline">{rating.reviewsLabel}</span>
        </span>

        <TrustpilotLogo />
      </a>
    </div>
  );
}
